import React from 'react';
import { Box, Container, Typography, Chip } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { seasonStatusLabel, seasonStatusChipStyle } from '../../utils/seasonStatus';
import { formatTeamSummaryLine } from '../../utils/teamClassification';

// Team Hub (030) — the private, authenticated workspace for a Season
// Team. Distinct from any future PUBLIC Team Page (out of scope here —
// see migration/report notes). This layout is purely presentational: it
// takes an already-loaded `team` row as a prop rather than fetching its
// own data, since every page that uses it already loads `team` for its
// own purposes (Overview needs staff/roster too, Roster/Staff need it
// for their own authorization checks) — one fetch per page, not two.
//
// Route shape stays under the EXISTING /my-teams/:teamId prefix (see
// App.js's own longstanding comment: /teams and /team/:teamId already
// belong to the NFL Stats Hub) — this is an additive nav layer over
// already-established routes, not a new top-level route family.
const HUB_TABS = [
  { key: 'overview', label: 'Overview',    path: '' },
  { key: 'roster',   label: 'Roster',      path: '/roster' },
  { key: 'testing',  label: 'Testing',     path: '/testing' },
  { key: 'staff',    label: 'Staff',       path: '/staff' },
  { key: 'schedule', label: 'Schedule',    path: '/schedule' },
  { key: 'board',    label: 'Team Board',  path: '/board' },
  { key: 'playbook', label: 'Playbook',    path: '/playbook' },
];

export default function TeamHubLayout({ team, active, children, maxWidth = 'md' }) {
  const navigate = useNavigate();
  const { teamId } = useParams();

  return (
    <Container maxWidth={maxWidth} sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 900 }}>{team.team_name}</Typography>
        {team.organization_name && (
          <Typography sx={{ color: '#9eb8d0', mt: 0.5 }}>{team.organization_name}</Typography>
        )}
        <Typography sx={{ color: '#6a8aa8', fontSize: '0.9rem', mt: 0.25 }}>
          {formatTeamSummaryLine(team)}
        </Typography>
        <Typography sx={{ color: '#6a8aa8', fontSize: '0.9rem' }}>
          {team.city}, {team.state} {team.zip_code}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
          {team.season_status && (
            <Chip size="small" label={seasonStatusLabel(team.season_status)} sx={{ fontWeight: 700, ...seasonStatusChipStyle(team.season_status) }} />
          )}
          {team.status === 'archived' && (
            <Chip size="small" label="Archived" sx={{ fontWeight: 700, bgcolor: '#2a1405', color: '#f5a623' }} />
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 0.5, mb: 3, overflowX: 'auto', borderBottom: '1px solid #1e2a3a' }}>
        {HUB_TABS.map(tab => {
          const isActive = tab.key === active;
          return (
            <Box
              key={tab.key}
              onClick={() => navigate(`/my-teams/${teamId}${tab.path}`)}
              sx={{
                px: 1.75, py: 1.25, cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 700, fontSize: '0.85rem',
                color: isActive ? '#2ea84b' : '#6a8aa8',
                borderBottom: isActive ? '2px solid #2ea84b' : '2px solid transparent',
                transition: 'color 0.15s ease',
                '&:hover': { color: isActive ? '#2ea84b' : '#9eb8d0' },
              }}
            >
              {tab.label}
            </Box>
          );
        })}
      </Box>

      {children}
    </Container>
  );
}
